/**
 * L'ÉCHAFAUDAGE — ce qui SITUE sans TITRER, déclaré UNE SEULE FOIS.
 *
 * Une parcelle d'échafaudage n'est pas une parcelle au sens du registre : elle
 * place un objet sur le sol pour qu'on puisse en parler, et s'efface devant le
 * levé. Elle se revendique, elle ne s'oppose pas.
 *
 * ⚠️⚠️ POURQUOI CE FICHIER EXISTE. Le littéral `'maille_systematique'` était
 * recopié à DIX-HUIT endroits — huit dans le service, quatre dans les écrans,
 * le reste en SQL. Quand une SECONDE origine d'échafaudage est apparue
 * (`derive_bati`, le parcellaire dérivé du bâti qui remplace le carroyage de
 * 25 m), il a fallu reprendre les dix-huit. Certains l'avaient déjà été, et
 * c'est précisément ce qui rend la chose dangereuse : rien ne distingue, à la
 * lecture, un endroit qui traite les deux origines d'un endroit qui n'en
 * traite qu'une. On ne voit pas ce qui manque.
 *
 * ⚠️ CE QUE L'OUBLI A COÛTÉ, ET IL NE S'AGISSAIT PAS DE COSMÉTIQUE.
 * `modules/parcelles.ts` refusait d'ACTIVER une maille — « une maille ne
 * s'active pas, c'est un échafaudage » — et laissait passer une parcelle
 * `derive_bati`. Une limite présomptive, tracée par l'axe médian entre deux
 * bâtiments, pouvait donc devenir OPPOSABLE d'un clic. Le foncier s'en
 * protégeait à l'étage au-dessus (`fon.garde_origine_indicative` refuse d'y
 * inscrire une propriété), mais la parcelle, elle, passait active.
 *
 * ⚠️ LA BASE EST LA RÉFÉRENCE, PAS CE FICHIER. `cad.est_echafaudage(origine)`
 * décide en SQL ; ceci en est la copie pour le service et les écrans, qui ne
 * peuvent pas interroger la base à chaque rendu. Deux copies d'une même règle
 * divergent au premier correctif appliqué à une seule — c'est pourquoi
 * `tests/echafaudage.test.ts` les CONFRONTE et refuse qu'elles s'écartent.
 */

/** Les origines qui situent sans titrer. La base en est la référence. */
export const ORIGINES_ECHAFAUDAGE = ['maille_systematique', 'derive_bati'] as const;

export type OrigineEchafaudage = (typeof ORIGINES_ECHAFAUDAGE)[number];

/**
 * ⚠️ TOLÈRE `null` ET `undefined` DÉLIBÉRÉMENT. Une origine absente n'est pas un
 * échafaudage : c'est une origine absente. Rendre `true` par prudence
 * bloquerait des parcelles réelles ; rendre `false` est le comportement juste,
 * et l'absence se traite là où elle se produit.
 */
export function estEchafaudage(origine: string | null | undefined): boolean {
  return origine != null && (ORIGINES_ECHAFAUDAGE as readonly string[]).includes(origine);
}

/**
 * CE QU'ON DIT À QUELQU'UN QUI VEUT INSCRIRE UN ÉCHAFAUDAGE AU PLAN.
 *
 * ⚠️ UN MESSAGE PAR ORIGINE, ET NON UN MESSAGE COMMUN. Les deux sont
 * présomptives pour des raisons différentes, et la démarche qui les lève n'est
 * pas la même : une maille vient d'un quadrillage arbitraire, une parcelle
 * dérivée vient d'une emprise bâtie réelle dont la limite est déduite. Dire
 * « c'est présomptif » aux deux ferait perdre l'information qui oriente vers
 * le bon geste.
 */
export const REFUS_ACTIVATION: Record<OrigineEchafaudage, string> = {
  maille_systematique:
    "Une maille systématique ne s'inscrit pas au plan. Faites-la lever : "
    + 'le levé produit une parcelle, et la maille cède.',
  derive_bati:
    "Cette parcelle est dérivée d'une emprise bâtie : sa limite est déduite, pas relevée. "
    + "Elle ne s'inscrit pas au plan en l'état — un levé contradictoire la rend opposable.",
};

/** L'étiquette courte, pour une liste ou une carte. */
export const ETIQUETTE_ECHAFAUDAGE: Record<OrigineEchafaudage, string> = {
  maille_systematique: 'maille — échafaudage',
  derive_bati: 'dérivée du bâti — échafaudage',
};
